/**
 * Visual Crossing Geo Services Integration for Route Weather
 * 
 * Provides geocoding, routing, and waypoint sampling for weather checks along routes.
 * Uses the Visual Crossing Geo Services API.
 * 
 * @see https://www.visualcrossing.com/resources/documentation/weather-api/geo-services-api/
 */

import { getServerRoot, buildGeoUrl } from './apiConfig.js';

/**
 * Calculate distance between two points in km using Haversine formula
 */
export function haversineDistance(point1, point2) {
  const R = 6371; // Earth's radius in km
  const dLat = (point2.lat - point1.lat) * Math.PI / 180;
  const dLng = (point2.lng - point1.lng) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(point1.lat * Math.PI / 180) * Math.cos(point2.lat * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Sample points along a route at regular intervals
 * If the route is too long for the given interval and maxPoints,
 * the interval is automatically increased to ensure equal spacing.
 * 
 * @param {Array<{lat: number, lng: number}>} points - Full route points
 * @param {number} intervalKm - Desired interval between samples in km
 * @param {number} maxPoints - Maximum number of sample points
 * @returns {Array<{lat: number, lng: number, distanceKm: number}>}
 */
export function sampleRoutePoints(points, intervalKm = 80, maxPoints = 50) {
  if (!points || points.length < 2) return points || [];

  // First pass: calculate total route distance
  let totalDistance = 0;
  for (let i = 1; i < points.length; i++) {
    totalDistance += haversineDistance(points[i - 1], points[i]);
  }

  // Calculate how many points we'd get with the desired interval
  // (excluding origin and destination which are always included)
  const intermediatePoints = Math.floor(totalDistance / intervalKm);
  const totalPointsNeeded = intermediatePoints + 2; // +2 for origin and destination

  // If we'd exceed maxPoints, adjust the interval for equal spacing
  let effectiveInterval = intervalKm;
  if (totalPointsNeeded > maxPoints) {
    // Distribute points evenly: (maxPoints - 2) intermediate points plus origin and destination
    effectiveInterval = totalDistance / (maxPoints - 1);
  }

  const samples = [];
  let accumulatedDistance = 0;
  let lastSampleDistance = 0;

  // Always include first point
  samples.push({ ...points[0], distanceKm: 0, isOrigin: true });

  for (let i = 1; i < points.length; i++) {
    const segmentDistance = haversineDistance(points[i - 1], points[i]);
    accumulatedDistance += segmentDistance;

    // Check if we've traveled enough since last sample
    if (accumulatedDistance - lastSampleDistance >= effectiveInterval) {
      samples.push({
        ...points[i],
        distanceKm: Math.round(accumulatedDistance)
      });
      lastSampleDistance = accumulatedDistance;

      // Limit number of samples (safety check)
      if (samples.length >= maxPoints - 1) break;
    }
  }

  // Always include last point if it's not already the last sample
  const lastPoint = points[points.length - 1];
  const lastSample = samples[samples.length - 1];
  // Compare by coordinates, not reference
  const isLastPointAlreadyIncluded = lastSample.lat === lastPoint.lat && lastSample.lng === lastPoint.lng;
  
  if (!isLastPointAlreadyIncluded) {
    samples.push({
      ...lastPoint,
      distanceKm: Math.round(totalDistance),
      isDestination: true
    });
  } else {
    // Mark the existing last sample as destination
    lastSample.isDestination = true;
  }

  return samples;
}

/**
 * Geocode an address to coordinates using Visual Crossing Geo API
 * 
 * @param {string} address - Address to geocode
 * @param {string} apiKey - Visual Crossing API key
 * @param {Object} options - Options
 * @param {string} [options.serverRoot] - Server root override
 * @returns {Promise<{lat: number, lng: number, displayName: string}>}
 */
export async function geocodeAddress(address, apiKey, options = {}) {
  if (!apiKey) {
    throw new Error('Visual Crossing API key required for geocoding');
  }

  // Check if already coordinates
  const coordMatch = address.match(/^(-?\d+\.?\d*),\s*(-?\d+\.?\d*)$/);
  if (coordMatch) {
    return {
      lat: parseFloat(coordMatch[1]),
      lng: parseFloat(coordMatch[2]),
      displayName: address
    };
  }

  const url = buildGeoUrl('geocode', { address, key: apiKey }, options.serverRoot);
  
  const response = await fetch(url);
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Geocoding failed: ${response.statusText}`);
  }

  const data = await response.json();
  if (!data.results || data.results.length === 0) {
    throw new Error(`No results found for: ${address}`);
  }

  const result = data.results[0];
  return {
    lat: result.latitude,
    lng: result.longitude,
    displayName: result.address?.label || result.title || address
  };
}

/**
 * Reverse geocode coordinates to an address using Visual Crossing Geo API
 * 
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @param {string} apiKey - Visual Crossing API key
 * @param {Object} options - Options
 * @param {string} [options.serverRoot] - Server root override
 * @returns {Promise<{displayName: string, address: object}>}
 */
export async function reverseGeocode(lat, lng, apiKey, options = {}) {
  if (!apiKey) {
    throw new Error('Visual Crossing API key required for reverse geocoding');
  }

  const url = buildGeoUrl('reverse', { lat, lon: lng, key: apiKey }, options.serverRoot);
  
  const response = await fetch(url);
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Reverse geocoding failed: ${response.statusText}`);
  }

  const data = await response.json();
  if (!data.results || data.results.length === 0) {
    throw new Error(`No address found for coordinates: ${lat}, ${lng}`);
  }

  const result = data.results[0];
  return {
    displayName: result.address?.label || result.title || `${lat}, ${lng}`,
    address: result.address
  };
}

/**
 * Parse a single route object from the API response
 * @param {Object} route - Raw route from API
 * @returns {Object} Parsed route with distance, duration, points, etc.
 */
function parseRouteObject(route) {
  // Extract all geometry points from all legs
  const points = [];
  if (route.legs) {
    for (const leg of route.legs) {
      if (leg.geometry) {
        for (const coord of leg.geometry) {
          // API returns [lon, lat], convert to {lat, lng}
          points.push({
            lat: coord[1],
            lng: coord[0]
          });
        }
      }
    }
  }

  return {
    distance: route.distanceKm || (route.distanceMeters / 1000),
    duration: route.durationMinutes || (route.durationSeconds / 60),
    distanceMeters: route.distanceMeters,
    durationSeconds: route.durationSeconds,
    distanceMiles: route.distanceMiles,
    points,
    legs: route.legs,
    raw: route
  };
}

/**
 * Get route between two or more points from Visual Crossing Geo API
 * 
 * @param {Array<{lat: number, lng: number}>} waypoints - Route waypoints (origin, [intermediates], destination)
 * @param {string} apiKey - Visual Crossing API key
 * @param {Object} options - Route options
 * @param {string} [options.mode='car'] - Travel mode: car, truck, walking, scooter
 * @param {number} [options.alternatives=1] - Number of alternative routes (1-5)
 * @param {boolean} [options.returnAllRoutes=false] - Return all alternative routes instead of just the first
 * @param {string} [options.serverRoot] - Server root override
 * @returns {Promise<{distance: number, duration: number, points: Array, alternatives?: Array}>}
 */
export async function getRoute(waypoints, apiKey, options = {}) {
  if (!apiKey) {
    throw new Error('Visual Crossing API key required');
  }

  if (waypoints.length < 2) {
    throw new Error('At least 2 waypoints required');
  }

  const { mode = 'car', alternatives = 1, returnAllRoutes = false, serverRoot } = options;

  // Build URL with origin and destination
  const origin = waypoints[0];
  const destination = waypoints[waypoints.length - 1];
  
  const params = {
    originLat: origin.lat,
    originLon: origin.lng,
    destLat: destination.lat,
    destLon: destination.lng,
    mode,
    alternatives,
    key: apiKey
  };
  
  // Add intermediate waypoints if any
  if (waypoints.length > 2) {
    const intermediates = waypoints.slice(1, -1);
    params.waypoints = intermediates.map(wp => `${wp.lat},${wp.lng}`).join(';');
  }
  
  const url = buildGeoUrl('route', params, serverRoot);

  const response = await fetch(url);

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Route request failed: ${response.statusText}`);
  }

  const data = await response.json();
  
  if (!data.routes || data.routes.length === 0) {
    throw new Error('No route found');
  }

  // Parse the primary route
  const primaryRoute = parseRouteObject(data.routes[0]);

  // If requested, parse and attach all alternative routes
  if (returnAllRoutes && data.routes.length > 1) {
    primaryRoute.alternatives = data.routes.slice(1).map((r, index) => ({
      ...parseRouteObject(r),
      alternativeIndex: index + 1
    }));
  }

  return primaryRoute;
}

/**
 * Generate weather check points along a route
 * 
 * @param {string} origin - Origin address or coordinates
 * @param {string} destination - Destination address or coordinates
 * @param {string} apiKey - Visual Crossing API key
 * @param {Object} options - Sampling options
 * @returns {Promise<Array<{location: string, lat: number, lng: number, type: string, distanceKm: number}>>}
 */
export async function generateRouteWeatherPoints(origin, destination, apiKey, options = {}) {
  const {
    intervalKm = 80,      // Sample every ~50 miles
    maxPoints = 15,       // Max intermediate points
    includeWaypoints = [], // Additional waypoints along route
    mode = 'car',          // Travel mode
    returnFullPath = false // Whether to return full route geometry
  } = options;

  // Geocode origin and destination
  const originCoords = await geocodeAddress(origin, apiKey);
  const destCoords = await geocodeAddress(destination, apiKey);

  // Build waypoints array
  const waypoints = [originCoords];
  
  // Add any intermediate waypoints
  for (const wp of includeWaypoints) {
    const wpCoords = await geocodeAddress(wp, apiKey);
    waypoints.push(wpCoords);
  }
  
  waypoints.push(destCoords);

  // Get route from Visual Crossing Geo API
  const route = await getRoute(waypoints, apiKey, { mode });

  // Sample points along the route
  const samplePoints = sampleRoutePoints(route.points, intervalKm, maxPoints);

  // Convert to weather check locations
  const weatherPoints = samplePoints.map((point, index) => {
    let type = 'waypoint';
    let name = `${point.distanceKm} km`;
    
    if (point.isOrigin) {
      type = 'origin';
      name = originCoords.displayName;
    } else if (point.isDestination) {
      type = 'destination';
      name = destCoords.displayName;
    }

    return {
      id: `wp-${index}`,
      location: `${point.lat.toFixed(4)},${point.lng.toFixed(4)}`,
      name,
      type,
      lat: point.lat,
      lng: point.lng,
      distanceKm: point.distanceKm
    };
  });

  // Return full path if requested
  if (returnFullPath) {
    return {
      weatherPoints,
      routePath: route.points, // Full geometry for map drawing
      totalDistance: route.distance,
      totalDuration: route.duration
    };
  }

  return weatherPoints;
}

/**
 * Check if a string looks like coordinates
 */
export function isCoordinates(str) {
  return /^-?\d+\.?\d*,\s*-?\d+\.?\d*$/.test(str?.trim() || '');
}

/**
 * Format distance for display
 */
export function formatDistance(km, unitGroup = 'metric') {
  if (unitGroup === 'us') {
    const miles = km * 0.621371;
    return `${Math.round(miles)} mi`;
  }
  return `${Math.round(km)} km`;
}

/**
 * Format duration for display
 */
export function formatDuration(minutes) {
  if (minutes < 60) {
    return `${Math.round(minutes)} min`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

/**
 * Get route with full details including weather points
 * Convenience function that combines routing and weather point generation
 * 
 * @param {string} origin - Origin address or coordinates
 * @param {string} destination - Destination address or coordinates
 * @param {string} apiKey - Visual Crossing API key
 * @param {Object} options - Options
 * @returns {Promise<{route: object, weatherPoints: Array}>}
 */
export async function getRouteWithWeatherPoints(origin, destination, apiKey, options = {}) {
  const {
    intervalKm = 80,
    maxPoints = 15,
    includeWaypoints = [],
    mode = 'car'
  } = options;

  // Geocode all locations
  const originCoords = await geocodeAddress(origin, apiKey);
  const destCoords = await geocodeAddress(destination, apiKey);
  
  const waypoints = [originCoords];
  for (const wp of includeWaypoints) {
    waypoints.push(await geocodeAddress(wp, apiKey));
  }
  waypoints.push(destCoords);

  // Get route
  const route = await getRoute(waypoints, apiKey, { mode });

  // Sample weather points
  const samplePoints = sampleRoutePoints(route.points, intervalKm, maxPoints);

  const weatherPoints = samplePoints.map((point, index) => {
    let type = 'waypoint';
    let name = `${point.distanceKm} km`;
    
    if (point.isOrigin) {
      type = 'origin';
      name = originCoords.displayName;
    } else if (point.isDestination) {
      type = 'destination';
      name = destCoords.displayName;
    }

    return {
      id: `wp-${index}`,
      location: `${point.lat.toFixed(4)},${point.lng.toFixed(4)}`,
      name,
      type,
      lat: point.lat,
      lng: point.lng,
      distanceKm: point.distanceKm
    };
  });

  return {
    route: {
      origin: originCoords,
      destination: destCoords,
      distance: route.distance,
      distanceMiles: route.distanceMiles,
      duration: route.duration,
      points: route.points
    },
    weatherPoints
  };
}

/**
 * Generate alternative routes with weather check points
 * Fetches up to 5 alternative routes and samples weather points on each
 * 
 * @param {string} origin - Origin address or coordinates
 * @param {string} destination - Destination address or coordinates  
 * @param {string} apiKey - Visual Crossing API key
 * @param {Object} options - Options
 * @returns {Promise<Array<{routePath, weatherPoints, totalDistance, totalDuration, alternativeIndex}>>}
 */
export async function generateAlternativeRoutes(origin, destination, apiKey, options = {}) {
  const {
    intervalKm = 80,
    maxPoints = 50,
    mode = 'car',
    serverRoot,
    numAlternatives = 3  // Request up to 3 alternatives by default
  } = options;

  // Geocode origin and destination
  const originCoords = await geocodeAddress(origin, apiKey, { serverRoot });
  const destCoords = await geocodeAddress(destination, apiKey, { serverRoot });

  // Get routes with alternatives
  const primaryRoute = await getRoute(
    [originCoords, destCoords], 
    apiKey, 
    { 
      mode, 
      alternatives: numAlternatives + 1,  // Request primary + alternatives
      returnAllRoutes: true,
      serverRoot 
    }
  );

  // Build array of all routes (primary + alternatives)
  const allRoutes = [
    { ...primaryRoute, alternativeIndex: 0 }  // Primary route
  ];
  
  if (primaryRoute.alternatives) {
    allRoutes.push(...primaryRoute.alternatives);
  }

  // Sample weather points on each route
  const routesWithWeatherPoints = allRoutes.map((route, idx) => {
    const samplePoints = sampleRoutePoints(route.points, intervalKm, maxPoints);
    
    const weatherPoints = samplePoints.map((point, pointIndex) => {
      let type = 'waypoint';
      let name = `${Math.round(point.distanceKm)} km`;
      
      if (point.isOrigin) {
        type = 'origin';
        name = originCoords.displayName;
      } else if (point.isDestination) {
        type = 'destination';
        name = destCoords.displayName;
      }

      return {
        id: `alt${idx}-wp-${pointIndex}`,
        location: `${point.lat.toFixed(4)},${point.lng.toFixed(4)}`,
        name,
        type,
        lat: point.lat,
        lng: point.lng,
        distanceKm: point.distanceKm
      };
    });

    return {
      alternativeIndex: route.alternativeIndex,
      routePath: route.points,
      weatherPoints,
      totalDistance: route.distance,
      totalDuration: route.duration,
      distanceMiles: route.distanceMiles
    };
  });

  return routesWithWeatherPoints;
}